import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Skeleton,
  Checkbox,
} from '@lead-magnifier/ui'
import { Download, Search, Users, Check, Clock, RefreshCw } from 'lucide-react'
import { useLeads, useLeadStats } from '@/hooks/use-leads'
import { useEvents } from '@/hooks/use-events'

export const Route = createFileRoute('/_authenticated/leads/')({
  component: LeadsPage,
})

function LeadsPage() {
  const [search, setSearch] = useState('')
  const [eventFilter, setEventFilter] = useState<string>('all')
  const [showConfirmedOnly, setShowConfirmedOnly] = useState(false)

  const { data: leads, isLoading, refetch, isFetching } = useLeads()
  const { data: events } = useEvents()
  const { data: stats } = useLeadStats()

  const filteredLeads = (leads || []).filter((lead) => {
    if (search && !lead.email.toLowerCase().includes(search.toLowerCase()) &&
        !lead.name?.toLowerCase().includes(search.toLowerCase())) {
      return false
    }
    if (eventFilter !== 'all' && lead.eventId !== eventFilter) {
      return false
    }
    if (showConfirmedOnly && !lead.doiConfirmed) {
      return false
    }
    return true
  })

  const exportCSV = () => {
    const headers = ['E-Mail', 'Name', 'Event', 'Status', 'Bestätigt am', 'Erstellt am']
    const rows = filteredLeads.map((lead) => [
      lead.email,
      lead.name || '',
      lead.eventName,
      lead.doiConfirmed ? 'Bestätigt' : 'Ausstehend',
      lead.doiConfirmedAt ? new Date(lead.doiConfirmedAt).toLocaleString('de-AT') : '',
      new Date(lead.createdAt).toLocaleString('de-AT'),
    ])

    const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n')
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `leads-export-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'gerade eben'
    if (minutes < 60) return `vor ${minutes} Min`
    if (hours < 24) return `vor ${hours} Std`
    if (days < 7) return `vor ${days} Tagen`
    return date.toLocaleDateString('de-AT')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Leads</h1>
          <p className="text-muted-foreground mt-1">
            Alle erfassten Kontakte deiner Events
          </p>
        </div>
        <Button onClick={exportCSV} disabled={filteredLeads.length === 0}>
          <Download className="h-4 w-4 mr-2" />
          CSV exportieren
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Gesamt</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Check className="h-3 w-3 text-green-600" />
              Bestätigt
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats?.confirmed || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3 text-yellow-600" />
              Ausstehend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats?.pending || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Conversion Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.conversionRate || 0}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Suche nach E-Mail oder Name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={eventFilter} onValueChange={setEventFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Alle Events" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Events</SelectItem>
                {events?.map((event) => (
                  <SelectItem key={event.id} value={event.id}>
                    {event.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Checkbox
                id="confirmed"
                checked={showConfirmedOnly}
                onCheckedChange={(checked) => setShowConfirmedOnly(checked === true)}
              />
              <label htmlFor="confirmed" className="text-sm cursor-pointer">
                Nur bestätigte
              </label>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <CardTitle className="mb-2">Keine Leads gefunden</CardTitle>
              <CardDescription>
                {search || eventFilter !== 'all' || showConfirmedOnly
                  ? 'Versuche andere Filterkriterien.'
                  : 'Teile deine QR-Codes, um Leads zu sammeln.'}
              </CardDescription>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Erstellt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell className="font-medium">{lead.email}</TableCell>
                    <TableCell>{lead.name || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{lead.eventName}</Badge>
                    </TableCell>
                    <TableCell>
                      {lead.doiConfirmed ? (
                        <Badge variant="default" className="bg-green-600">
                          <Check className="h-3 w-3 mr-1" />
                          Bestätigt
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <Clock className="h-3 w-3 mr-1" />
                          Ausstehend
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(lead.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
